import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { buildDemoState } from './demoData';
import { ALL_VERIFY_ITEMS, ALL_GUARD_ITEMS } from './checklists';

const KEY = 'nikahrapi_v1';
const StoreCtx = createContext(null);

export const uid = () => Math.random().toString(36).slice(2, 10);

const BULAN = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];

export const fmtIDR = (n) => 'Rp' + Number(n || 0).toLocaleString('id-ID');

export const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso + 'T00:00:00');
  return `${d.getDate()} ${BULAN[d.getMonth()]}`;
};

export const todayISO = () => new Date().toISOString().slice(0, 10);

export function daysUntil(iso) {
  if (!iso) return Infinity;
  const a = new Date(iso + 'T00:00:00');
  const b = new Date();
  b.setHours(0, 0, 0, 0);
  return Math.round((a - b) / 86400000);
}

export const hMinus = (iso) => {
  const n = daysUntil(iso);
  if (!isFinite(n)) return '';
  if (n === 0) return 'Hari H';
  return n > 0 ? `H-${n} hari` : `H+${-n} hari`;
};

export function vendorStats(state, vendorId) {
  const rec = state.verifications[vendorId] || {};
  const total = ALL_VERIFY_ITEMS.length;
  const checked = ALL_VERIFY_ITEMS.filter((i) => rec[i.key]?.checked).length;
  return { total, checked, remaining: total - checked };
}

export function vendorUnchecked(state, vendorId) {
  const rec = state.verifications[vendorId] || {};
  return ALL_VERIFY_ITEMS.filter((i) => !rec[i.key]?.checked);
}

export function guardStats(state, paymentId) {
  const checks = state.guardChecks[paymentId] || {};
  const total = ALL_GUARD_ITEMS.length;
  const checked = ALL_GUARD_ITEMS.filter((i) => checks[i.key]).length;
  return { total, checked, remaining: total - checked };
}

export function nextPaymentForVendor(state, vendorId) {
  return state.payments
    .filter((p) => p.vendorId === vendorId && p.status === 'upcoming')
    .sort((a, b) => (a.dueDate < b.dueDate ? -1 : 1))[0];
}

export function nextActions(state) {
  if (!state) return [];
  const acts = [];
  const soon = state.payments
    .filter((p) => p.status === 'upcoming')
    .sort((a, b) => (a.dueDate < b.dueDate ? -1 : 1));

  for (const p of soon) {
    const v = state.vendors.find((x) => x.id === p.vendorId);
    if (!v) continue;
    const ver = state.verifications[p.vendorId] || {};
    const un = (k) => !ver[k]?.checked;
    const near = daysUntil(p.dueDate) <= 30;
    const vTo = `/app/vendor/${v.id}`;
    const gTo = `/app/pembayaran/${p.id}/guard`;

    if (near && (un('scope') || un('deliverables') || un('jadwal_bayar')))
      acts.push({ prio: 1, key: `p1-${p.id}`, title: `Konfirmasi kontrak ${v.category} sebelum DP berikutnya`, desc: `${p.label} ${fmtIDR(p.amount)} jatuh tempo ${fmtDate(p.dueDate)}. Beberapa hal kontrak masih perlu dicek.`, cta: 'Cek Sekarang', to: vTo });
    if (near && (un('nama_penerima') || un('rek_penerima') || un('penerima_konfirmasi')))
      acts.push({ prio: 2, key: `p2-${p.id}`, title: `Konfirmasi penerima pembayaran ${v.name}`, desc: `${p.label} ${fmtIDR(p.amount)} jatuh tempo ${fmtDate(p.dueDate)}. Nama penerima belum dikonfirmasi ke PIC.`, cta: 'Buka Payment Guard', to: gTo });
    if (near && un('jadwal_bayar'))
      acts.push({ prio: 3, key: `p3-${p.id}`, title: `Minta jadwal pembayaran tertulis ${v.category}`, desc: `Tahapan pembayaran ${v.name} belum tertulis jelas.`, cta: 'Cek Sekarang', to: vTo });
    if (un('ketentuan_pembatalan') || un('ketentuan_perubahan'))
      acts.push({ prio: 4, key: `p4-${v.id}`, title: `Tinjau ketentuan pembatalan ${v.category}`, desc: `Ketentuan perubahan / pembatalan ${v.name} belum ditinjau.`, cta: 'Lihat Vendor', to: vTo });
    if (un('nama_bisnis') || un('pic_jelas') || un('kontak_aktif'))
      acts.push({ prio: 5, key: `p5-${v.id}`, title: `Lengkapi identitas & PIC ${v.category}`, desc: `Informasi dasar ${v.name} belum lengkap.`, cta: 'Lihat Vendor', to: vTo });
    if (un('portfolio_dilihat') || un('referensi'))
      acts.push({ prio: 6, key: `p6-${v.id}`, title: `Cek portfolio & referensi ${v.category}`, desc: `Portfolio atau referensi ${v.name} belum diperiksa.`, cta: 'Lihat Vendor', to: vTo });
    if (un('bukti_sebelumnya') && state.payments.some((x) => x.vendorId === v.id && x.status === 'paid'))
      acts.push({ prio: 7, key: `p7-${v.id}`, title: `Simpan catatan bukti pembayaran ${v.category}`, desc: `Bukti pembayaran sebelumnya untuk ${v.name} belum tercatat.`, cta: 'Lihat Vendor', to: vTo });
  }

  state.tasks
    .filter((t) => !t.done && daysUntil(t.targetDate) <= 14)
    .sort((a, b) => (a.targetDate < b.targetDate ? -1 : 1))
    .forEach((t) =>
      acts.push({ prio: 8, key: `p8-${t.id}`, title: t.title, desc: `Persiapan · target ${fmtDate(t.targetDate)}`, cta: 'Lihat Persiapan', to: '/app/persiapan' }),
    );

  const seen = new Set();
  return acts
    .filter((a) => (seen.has(a.key) ? false : seen.add(a.key)))
    .sort((a, b) => a.prio - b.prio)
    .slice(0, 3);
}

export function StoreProvider({ children }) {
  const [state, setState] = useState(() => {
    try {
      const raw = localStorage.getItem(KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    try {
      if (state) localStorage.setItem(KEY, JSON.stringify(state));
      else localStorage.removeItem(KEY);
    } catch { /* ignore */ }
  }, [state]);

  const api = useMemo(
    () => ({
      state,
      hasProfile: !!state?.profile,
      startDemo: () => setState(buildDemoState()),
      startReal: (p) =>
        setState({ profile: { ...p, demoMode: false, createdAt: todayISO() }, vendors: [], verifications: {}, payments: [], guardChecks: {}, tasks: [] }),
      resetAll: () => setState(null),
      addVendor: (v) =>
        setState((s) => ({ ...s, vendors: [...s.vendors, { ...v, id: uid(), createdAt: todayISO() }] })),
      toggleVerify: (vendorId, key) =>
        setState((s) => {
          const rec = { ...(s.verifications[vendorId] || {}) };
          const cur = rec[key];
          rec[key] = cur?.checked ? { checked: false } : { checked: true, checkedAt: todayISO(), note: cur?.note || '' };
          return { ...s, verifications: { ...s.verifications, [vendorId]: rec } };
        }),
      setVerifyNote: (vendorId, key, note) =>
        setState((s) => {
          const rec = { ...(s.verifications[vendorId] || {}) };
          rec[key] = { checked: !!rec[key]?.checked, checkedAt: rec[key]?.checkedAt, note };
          return { ...s, verifications: { ...s.verifications, [vendorId]: rec } };
        }),
      addPayment: (p) =>
        setState((s) => ({ ...s, payments: [...s.payments, { ...p, id: uid(), status: 'upcoming', createdAt: todayISO() }] })),
      setGuardCheck: (pid, key, val) =>
        setState((s) => ({
          ...s,
          guardChecks: { ...s.guardChecks, [pid]: { ...(s.guardChecks[pid] || {}), [key]: val } },
        })),
      recordPayment: (pid, rec) =>
        setState((s) => ({
          ...s,
          payments: s.payments.map((p) =>
            p.id === pid ? { ...p, status: 'paid', amount: rec.amount, paidAt: rec.date, label: rec.stage || p.label, reference: rec.reference, note: rec.note } : p,
          ),
        })),
      setDecisionNote: (pid, text) =>
        setState((s) => ({
          ...s,
          payments: s.payments.map((p) => (p.id === pid ? { ...p, decisionNote: { text, at: todayISO() } } : p)),
        })),
      addTask: (t) => setState((s) => ({ ...s, tasks: [...s.tasks, { ...t, id: uid(), done: false }] })),
      setLesson: (text) => setState((s) => ({ ...s, lesson: text })),
      toggleTask: (id) =>
        setState((s) => ({
          ...s,
          tasks: s.tasks.map((t) => (t.id === id ? { ...t, done: !t.done, doneAt: !t.done ? todayISO() : undefined } : t)),
        })),
    }),
    [state],
  );

  return <StoreCtx.Provider value={api}>{children}</StoreCtx.Provider>;
}

export const useStore = () => useContext(StoreCtx);
